import { DatabaseIcon, SparklesIcon } from "@heroicons/react/outline";
import {
  Braces,
  ChevronDownIcon,
  FolderOpenIcon,
  LinkIcon,
  Table2Icon,
  UploadIcon,
} from "lucide-react";
import { useState } from "react";

import { VariableSet } from "../../../../Playground/types";

import { AIImport } from "./AIImport";
import { DatasetImport } from "./DatasetImport";
import { LibraryImport } from "./LibraryImport";
import { RawJsonImport } from "./RawJsonImport";
import { RequestImport } from "./RequestImport";
import { TableRowsImport } from "./TableRowsImport";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { RequestToolData } from "@/queries/requests/useRequestLogInputVariables";

interface OpenLibraryButtonProps {
  onVariablesFetched?: (variables: Record<string, string>, setName?: string) => void;
  onRequestImported?: (
    requestId: number,
    variables: Record<string, string>,
    toolData?: RequestToolData | null,
  ) => void;
  normalizeImportedVariables?: (variables: Record<string, string>) => Record<string, string>;
  disabled?: boolean;
  enableAIDataset?: boolean;
  promptTemplate?: string;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
  activeSetName?: string;
  variableNames?: string[];
}

const OpenLibraryButton = ({
  onVariablesFetched,
  onRequestImported,
  normalizeImportedVariables,
  disabled = false,
  enableAIDataset = false,
  promptTemplate = "",
  onAddVariableSet,
  currentVariableSetCount = 0,
  activeSetName,
  variableNames,
}: OpenLibraryButtonProps) => {
  const [isLibraryDialogOpen, setIsLibraryDialogOpen] = useState(false);
  const [isDatasetDialogOpen, setIsDatasetDialogOpen] = useState(false);
  const [isTableRowsDialogOpen, setIsTableRowsDialogOpen] = useState(false);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [isRawJsonDialogOpen, setIsRawJsonDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <>
      <DropdownMenu onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" tabIndex={-1} disabled={disabled}>
            <UploadIcon className="h-4 w-4 text-gray-500" />
            <span>Import values</span>
            <ChevronDownIcon
              className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-1.5">
          {activeSetName && (
            <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
              Import into <span className="text-gray-700">{activeSetName}</span>
            </div>
          )}
          <DropdownMenuItem
            onSelect={() => setIsLibraryDialogOpen(true)}
            className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
          >
            <FolderOpenIcon className="mt-0.5 h-4 w-4 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">Saved input set</div>
              <div className="text-xs text-gray-500">Reuse values from your input-set library.</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsRequestDialogOpen(true)}
            className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
          >
            <LinkIcon className="mt-0.5 h-4 w-4 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">Request log</div>
              <div className="text-xs text-gray-500">Pull values from a previous request.</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsTableRowsDialogOpen(true)}
            className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
          >
            <Table2Icon className="mt-0.5 h-4 w-4 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">Table rows</div>
              <div className="text-xs text-gray-500">Create sets from rows in a table.</div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsDatasetDialogOpen(true)}
            className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
          >
            <DatabaseIcon className="mt-0.5 h-4 w-4 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">Dataset rows</div>
              <div className="text-xs text-gray-500">
                Create sets from selected dataset examples.
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsRawJsonDialogOpen(true)}
            className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
          >
            <Braces className="mt-0.5 h-4 w-4 text-gray-400" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-800">Raw JSON</div>
              <div className="text-xs text-gray-500">Paste a JSON object to create variables.</div>
            </div>
          </DropdownMenuItem>
          {enableAIDataset && (
            <DropdownMenuItem
              onSelect={() => setIsAIDialogOpen(true)}
              className="w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2"
            >
              <SparklesIcon className="mt-0.5 h-4 w-4 text-gray-400" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800">Generate with AI</div>
                <div className="text-xs text-gray-500">Draft realistic values for this prompt.</div>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <LibraryImport
        isOpen={isLibraryDialogOpen}
        onOpenChange={setIsLibraryDialogOpen}
        onVariablesFetched={onVariablesFetched}
      />

      <TableRowsImport
        isOpen={isTableRowsDialogOpen}
        onOpenChange={setIsTableRowsDialogOpen}
        onVariablesFetched={onVariablesFetched}
        onAddVariableSet={onAddVariableSet}
        currentVariableSetCount={currentVariableSetCount}
        variableNames={variableNames}
      />

      <DatasetImport
        isOpen={isDatasetDialogOpen}
        onOpenChange={setIsDatasetDialogOpen}
        onVariablesFetched={onVariablesFetched}
        onAddVariableSet={onAddVariableSet}
        currentVariableSetCount={currentVariableSetCount}
      />

      <RequestImport
        isOpen={isRequestDialogOpen}
        onOpenChange={setIsRequestDialogOpen}
        onVariablesFetched={onVariablesFetched}
        onRequestImported={onRequestImported}
        normalizeImportedVariables={normalizeImportedVariables}
      />

      <RawJsonImport
        isOpen={isRawJsonDialogOpen}
        onOpenChange={setIsRawJsonDialogOpen}
        onVariablesFetched={onVariablesFetched}
      />

      {enableAIDataset && (
        <AIImport
          isOpen={isAIDialogOpen}
          onOpenChange={setIsAIDialogOpen}
          onVariablesFetched={onVariablesFetched}
          promptTemplate={promptTemplate}
        />
      )}
    </>
  );
};

export default OpenLibraryButton;
